import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const variants = {
  success: {
    container: 'bg-green-50 border-green-200',
    icon: 'text-green-400',
    title: 'text-green-800',
    text: 'text-green-700',
    Icon: CheckCircleIcon,
  },
  error: {
    container: 'bg-red-50 border-red-200',
    icon: 'text-red-400',
    title: 'text-red-800',
    text: 'text-red-700',
    Icon: XCircleIcon,
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200',
    icon: 'text-yellow-400',
    title: 'text-yellow-800',
    text: 'text-yellow-700',
    Icon: ExclamationTriangleIcon,
  },
  info: {
    container: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-400',
    title: 'text-blue-800',
    text: 'text-blue-700',
    Icon: InformationCircleIcon,
  },
};

export function Alert({
  type = 'info',
  title,
  children,
  onClose,
  className,
}) {
  const { t } = useTranslation();
  const variant = variants[type];
  const Icon = variant.Icon;

  return (
    <div
      className={twMerge(
        clsx(
          'rounded-md border p-4',
          variant.container
        ),
        className
      )}
      role="alert"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={clsx('h-5 w-5', variant.icon)} aria-hidden="true" />
        </div>
        <div className="ms-3 flex-1">
          {title && (
            <h3 className={clsx('text-sm font-medium', variant.title)}>
              {title}
            </h3>
          )}
          {children && (
            <div className={clsx('text-sm', variant.text, title && 'mt-2')}>
              {children}
            </div>
          )}
        </div>
        {onClose && (
          <div className="ms-auto ps-3">
            <button
              type="button"
              className={clsx(
                'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                variant.text,
                'hover:bg-white/50'
              )}
              onClick={onClose}
            >
              <span className="sr-only">{t('common.dismiss')}</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Alert;
